export enum Division {
  Kingspan = 'kingspan',
  Joriside = 'joriside',
  Us = 'us',
}

export enum ProjectStatus {
  InProgress = 'InProgress',
  Completed = 'Completed',
}

export enum Application {
  WallPanel = 'WallPanel',
  RoofPanel = 'RoofPanel',
}

export enum CalculationType {
  PanelCheck = 'PanelCheck',
  FastenerCheck = 'FastenerCheck',
}

export enum CalculationStatus {
  InProgress = 'InProgress',
  Fail = 'Fail',
  Pass = 'Pass',
  Calculating = 'Calculating',
  Expired = 'Expired',
  FailedToCalculate = 'FailedToCalculate',
  Completed = 'Completed',
}

export enum LoadType {
  NotRequired = 'NotRequired',
  PermanentLoad = 'PermanentLoad',
  SnowLoad = 'SnowLoad',
  PressureLoad = 'PressureLoad',
  SuctionLoad = 'SuctionLoad',
  ImposedLoad = 'ImposedLoad',
  ConstructionLoad = 'ConstructionLoad',
  ExcepSnowLoad = 'ExcepSnowLoad',
}

export type BaseAudit = {
  createdBy: string;
  createdByName: string;
  createdOn: string;
  modifiedBy: string;
  modifiedByName: string;
  modifiedOn: string;
};

export type TemperatureDetails = {
  summerExternalCustom?: number;
  summerExternalColor?: SummerExternalColor;
  summerInternal: number;
  winterExternal: number;
  winterInternal: number;
};

export type SummerExternalColor = {
  id: string;
  label: string;
  value: number;
};

export enum SummerExternalType {
  SelectColor = 'SelectColor',
  EnterCustom = 'EnterCustom',
}

type Load = {
  id?: string;
};

export type PointLoad = Load & {
  load: number;
  position: number;
};

export type DistributedLoad = Load & {
  loadStart: number;
  loadEnd: number;
  positionStart: number;
  positionEnd: number;
};

export type Loads = {
  uniformLoad?: number;
  pointLoads?: PointLoad[];
  distributedLoads?: DistributedLoad[];
  selfWeight?: number;
};

export enum LoadCalcType {
  WallPanel,
  RoofPanel,
}

export type SpansAndLoads = {
  loadsCalculationType: LoadCalcType;
  spans: number[];
  leftCantilever?: number;
  rightCantilever?: number;
  permanentLoads?: Loads;
  snowLoads?: Loads;
  pressureLoads?: Loads;
  suctionLoads?: Loads;
  imposedLoads?: Loads;
  constructionLoads?: Loads;
  excepSnowLoads?: Loads;
};

export type ProductDetails = {
  id: string;
  division: string;
  panelType: string;
  panelThickness: number;
  externalProfile: string;
  externalFaceThickness: number;
  externalGrade: string;
  internalProfile: string;
  internalFaceThickness: number;
  internalGrade: string;
  panelWidthForFixingCalculations: number;
};

export type ProductMetadata = {
  minEndSupportRoof?: number;
  minIntermediateSupportRoof?: number;
  minEndSupportWall?: number;
  minIntermediateSupportWall?: number;
  selfWeight?: number;
  connectionStrength?: number;
  panelWidthForFixingCalculations?: number;
};

export type FullProduct = ProductDetails & ProductMetadata;

export type Fasteners = {
  pullOutDesignResistance?: number;
  pullOverDesignResistance?: number;
  // Recommended Fastener Fields
  manufacturerName?: string;
  fastenerName?: string;
  supportMaterial?: string;
  gauge?: string;
  washerType?: string;
  fastenerMaterial?: string;
  screwDiameter?: string;
  testStandard?: string;
  notes?: string;
};

export type Zone = {
  id: string;
  name: string;
  spans: number;
  spanLength: number;
  suction: number;
};

export type ZoneDetails = BaseAudit & {
  zones: Zone[];
};

export type Calculation = {
  id: string;
  details: CalculationDetails & BaseAudit;
  standard?: Standard & BaseAudit;
  status: CalculationStatus;
  temperature?: TemperatureDetails & BaseAudit;
  spansAndLoads?: SpansAndLoads & BaseAudit;
  product?: ProductDetails & ProductMetadata & BaseAudit;
  fasteners?: Fasteners & BaseAudit;
  manufacturer?: ManufacturerFile; // TODO: do we want to send the entire file || results after saving one
  resultExpired: boolean;
  result?: CalculationResults;
  supportWidth: SupportWidth;
  zoneDetails?: ZoneDetails;
} & BaseAudit;

export enum CombinationCalculationType {
  Deflection = 'Deflection',
  Reaction = 'Reaction',
  ReactionUls = 'ReactionUls',
  Shear = 'Shear',
  ShearUls = 'ShearUls',
  WrinklingYieldingExternal = 'WrinklingYieldingExternal',
  WrinklingYieldingExternalUls = 'WrinklingYieldingExternalUls',
  WrinklingYieldingInternal = 'WrinklingYieldingInternal',
  WrinklingYieldingInternalUls = 'WrinklingYieldingInternalUls',
}

export type CalculationResults = {
  id: string;
  criteria: CriteriaResult[];
  supports: SupportResult[];
  timeRetrieved: string;
  hasFastenerResults: boolean;
  zones?: ZoneResult[];
};

export type CriteriaResult = {
  calculationType: CombinationCalculationType;
  result: number;
  isPass: boolean;
};

export type SupportResult = {
  supportNumber: number;
  minReaction: number;
  numberOfFasteners?: number;
  minWidth: number;
};

export type ZoneResult = {
  name: string;
  numberOfSpans: number;
  spanLength: number;
  minEndReaction: number;
  minIntermediateReaction?: number;
  endFasteners?: number;
  intermediateFasteners?: number;
};

export type CalculationDetails = {
  type: CalculationType;
  application: Application;
  name: string;
  description?: string;
};

export enum SortBy {
  LastModified = 'LastModified',
  DateCreated = 'DateCreated',
  ProjectName = 'ProjectName',
  EngineerName = 'EngineerName',
}

export enum SortDirection {
  Ascending = 'Ascending',
  Descending = 'Descending',
}

export type SupportWidth = {
  end: number;
  intermediate: number;
};

export type Project = {
  id: string;
  name: string;
  engineer: string;
  client?: string;
  description?: string;
  reference?: string;
  notes?: string;
  status: ProjectStatus;
  standard?: Standard;
  calculations: Calculation[];
  division: string;
} & BaseAudit;

export type EditProject = Omit<Project, keyof BaseAudit | 'calculations'>;

export type NewProject = Omit<EditProject, 'id'>;

export type PagedResponse = {
  total: number;
  totalPages: number;
  pageSize: number;
  pageNumber: number;
  data: Project[];
};

export type Standard = {
  id?: string;
  supportWidth?: SupportWidth;
  country?: string;
  reference?: string;
  annexe?: string;
  constructionClass?: string;
  paramsAndCoefficients?: string;
  loadFactors?: string;
  deflectionLimitRoof?: DeflectionLimitRoof | null;
  deflectionLimitWall?: DeflectionLimitWall | null;
  createdBy?: string;
  createdByName?: string;
  createdOn?: string;
  modifiedBy?: string;
  modifiedByName?: string;
  modifiedOn?: string;
};

export type UserStandard = Standard & {
  user: string;
};

export type UserDetails = {
  name: string;
  email: string;
};

export interface IOption {
  name: string;
}

export interface IProductOption {
  value: string | number;
}

export type PanelType = IProductOption & {
  panelThicknesses: PanelThickness[];
};

export type PanelThickness = IProductOption & {
  externalProfiles: ExternalProfile[];
};

export type ExternalProfile = IProductOption & {
  externalFaceThicknesses: ExternalFaceThickness[];
};

export type ExternalFaceThickness = IProductOption & {
  externalGrades: ExternalGrade[];
};

export type ExternalGrade = IProductOption & {
  internalProfiles: InternalProfile[];
};

export type InternalProfile = IProductOption & {
  internalFaceThicknesses: InternalFaceThickness[];
};

export type InternalFaceThickness = IProductOption & {
  internalGrades: InternalGrade[];
};

export type InternalGrade = IProductOption & {
  panelWidthsForFixingCalculations: PanelWidthForFixingCalculations[];
};

export type PanelWidthForFixingCalculations = IProductOption & {
  productId: string;
  productName: string;
  coreThickness: number;
  minEndSupportRoof: number;
  minIntermediateSupportRoof: number;
  minEndSupportWall: number;
  minIntermediateSupportWall: number;
  connectionStrength: number;
  selfWeight: number;
};

export type Standards = IOption & {
  references: Reference[];
  deflectionLimitRoof: DeflectionLimitRoof;
  deflectionLimitWall: DeflectionLimitWall;
};

export type Reference = IOption & {
  annexes: Annexe[];
};

export type Annexe = IOption & {
  constructionClasses: ConstructionClass[];
};

export type ConstructionClass = IOption & {
  paramsAndCoefficients: ParamAndCoefficient[];
};

export type Psi = {
  snow: number;
  snowCoefficient?: number;
  wind: number;
  windCoefficient?: number;
  temperature: number;
  temperatureCoefficient?: number;
  live: boolean;
};

export type ParamAndCoefficient = IOption & {
  psi0: Psi;
  psi1: Psi;
  loadFactors: LoadFactor[];
};

export type PartialFactor = {
  permanentLoadFavourable: number;
  permanentLoadUnfavourable: number;
  variableAction: number;
  temperature: number;
  creepEffectPermanent: number;
  creepEffectSnow: number;
};

export type LoadFactor = IOption & {
  id: string;
  uls: PartialFactor;
  sls: PartialFactor;
};

export type DeflectionLimitWall = IOption & {
  pressureLoad: number;
  suctionLoad: number;
  cantileverLoad: number;
  customized?: boolean;
};

export type DeflectionLimitRoof = DeflectionLimitWall & {
  pressureLoadWithCreep: number;
  cantileverLoadWithCreep: number;
};

export type CalculationReportSelection = {
  id: string;
  name: string;
};

export type ManufacturerFileRow = {
  manufacturer: string;
  fastenerMaterial: string;
  supportMaterial: 'Steel' | 'Timber';
  gauge: string;
  applicationType: 'Wall' | 'Roof';
  washerType: string;
  fastenerName: string;
  screwDiameter: string;
  fastener: string;
  pullOutDesignResistance: string;
  panelType: string;
  preSelectedProduct: 'Y' | 'N';
  externalFaceThickness: string;
  pullOverDesignResistance: string;
  testStandard: string;
  notes: string;
};

export interface IFastenerOption {
  value: string;
}

export type ManufacturerFile = IFastenerOption & {
  supportMaterials: SupportMaterial[];
};

export type SupportMaterial = IFastenerOption & {
  gauges: Gauge[];
};

export type Gauge = IFastenerOption & {
  washerTypes: WasherType[];
};

export type WasherType = IFastenerOption & {
  fastenerMaterials: FastenerMaterial[];
};

export type FastenerMaterial = IFastenerOption & {
  screwDiameters: ScrewDiameter[];
};

export type ScrewDiameter = IFastenerOption & {
  pullOutDesignResistance: number;
  pullOverDesignResistance: number;
  fastenerId: string;
  fastenerName: string;
  applicationType: string;
  panelType: string;
  preSelectedProduct: string;
  externalFaceThickness: string;
  testStandard: string;
  notes: string;
};

export enum CalculationDialogMode {
  Add = 'add',
  Edit = 'edit',
  Duplicate = 'duplicate',
}

export function isRoofDeflectionLimit(data: DeflectionLimitWall | DeflectionLimitRoof): data is DeflectionLimitRoof {
  return 'pressureLoadWithCreep' in data && 'cantileverLoadWithCreep' in data;
}
